import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { Limits } from "@/app/vars/Limits";
import { CookieKey, SIDEBAR_COOKIE_MAX_AGE } from "@/app/vars/Storage";

export type SidebarState = {
  isOpen: boolean;
  initialized: boolean;
  onInit(): void;
  openSidebar(): void;
  closeSidebar(): void;
  closeSidebarOnMobile(): void;
  toggleSidebar(): void;
};

function readSidebarCookie(): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${CookieKey.SIDEBAR_OPEN}=([^;]+)`));
  return match ? match[1] : null;
}

function writeSidebarCookie(value: boolean): void {
  document.cookie = `${CookieKey.SIDEBAR_OPEN}=${value}; path=/; max-age=${SIDEBAR_COOKIE_MAX_AGE}; samesite=lax`;
}

function syncSidebarAttributes(isOpen: boolean): void {
  const isMobile = window.innerWidth < Limits.SIDEBAR_MOBILE_MAX_WIDTH;
  document.documentElement.dataset.sidebarOpen = String(isOpen);
  document.documentElement.dataset.sidebarMobileOpen = String(isOpen && isMobile);
}

export const SidebarStore = create<SidebarState>()(
  devtools((set, getState) => ({
    isOpen: true,
    initialized: false,

    onInit: () => {
      if (window.innerWidth < Limits.SIDEBAR_MOBILE_MAX_WIDTH) {
        set({ isOpen: false, initialized: true });
        syncSidebarAttributes(false);
        return;
      }

      if (readSidebarCookie() === null) {
        writeSidebarCookie(true);
      }

      const isOpen = readSidebarCookie() === "true";
      set({ isOpen, initialized: true });
      syncSidebarAttributes(isOpen);
    },

    openSidebar: () => {
      set({ isOpen: true });
      writeSidebarCookie(true);
      syncSidebarAttributes(true);
    },

    closeSidebar: () => {
      set({ isOpen: false });
      writeSidebarCookie(false);
      syncSidebarAttributes(false);
    },

    closeSidebarOnMobile: () => {
      if (!getState().isOpen) {
        return;
      }

      if (window.innerWidth < Limits.SIDEBAR_MOBILE_MAX_WIDTH) {
        getState().closeSidebar();
      }
    },

    toggleSidebar: () => {
      const isOpen = !getState().isOpen;
      set({ isOpen });
      writeSidebarCookie(isOpen);
      syncSidebarAttributes(isOpen);
    },
  })),
);

export function useSidebarOpen(initialOpen: boolean): boolean {
  const isOpen = SidebarStore((state) => state.isOpen);
  const initialized = SidebarStore((state) => state.initialized);

  return initialized ? isOpen : initialOpen;
}
